import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { EntryTable } from "@/components/bank-reconciliation/entry-table";
import { NominalUpload, DeletePeriodButton } from "@/components/bank-reconciliation/import-actions";
import {
  Landmark, Loader2, Upload, CalendarPlus, RefreshCw, Download, AlertTriangle, ShieldCheck, FileDown, RotateCcw, FolderOpen,
} from "lucide-react";
import {
  apiUrl, brFetch, formatPence, poundsToPence, selectionTotals, STATUS_LABEL,
  type Detail, type PeriodList, type Suggestion,
} from "@/lib/bank-reconciliation";

const inputCls = "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50";
const card = "bg-white rounded-xl border border-slate-200";

function Stat({ label, value, id, tone }: { label: string; value: string; id: string; tone?: string }) {
  return (
    <div className="px-3 py-2" data-testid={`stat-${id}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-duffells-grey/60">{label}</div>
      <div className={`font-mono text-sm font-bold ${tone ?? ""}`}>{value}</div>
    </div>
  );
}

export default function BankReconciliationPage() {
  const { user } = useAuth();
  const userId = user?.id;
  const { toast } = useToast();
  const [list, setList] = useState<PeriodList | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  const [selId, setSelId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [month, setMonth] = useState("");
  // decision form
  const [notes, setNotes] = useState("");
  const [channel, setChannel] = useState("");
  const [owner, setOwner] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [evidence, setEvidence] = useState("");
  const [closing, setClosing] = useState("");
  const [coverage, setCoverage] = useState(false);
  const [signNotes, setSignNotes] = useState("");
  const [reopenNotes, setReopenNotes] = useState("");

  const loadList = useCallback(async () => {
    try { setList(await brFetch<PeriodList>("/periods")); setListErr(null); }
    catch (e: any) { setListErr(e.message); }
  }, []);
  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try { setDetail(await brFetch<Detail>(`/periods/${id}`)); setDetailErr(null); }
    catch (e: any) { setDetailErr(e.message); }
    finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { if (userId != null) void loadList(); }, [userId, loadList]);
  useEffect(() => { setSel(new Set()); setWarnings([]); if (selId != null) void loadDetail(selId); else setDetail(null); }, [selId, loadDetail]);

  const run = async (key: string, fn: () => Promise<void>, ok: string) => {
    setBusy(key);
    try { await fn(); toast({ title: ok }); }
    catch (e: any) { toast({ title: e.message, variant: "destructive" }); }
    finally { setBusy(null); }
  };
  const refresh = async () => { await loadList(); if (selId != null) await loadDetail(selId); };
  const post = (path: string, json: unknown) => brFetch<any>(path, { method: "POST", json });

  const access = detail?.access ?? list?.access ?? { manage: false, signoff: false };
  const signed = detail?.period.status === "signed";
  const canManage = access.manage && !signed;

  const bank = useMemo(() => detail?.entries.filter((e) => e.side === "bank") ?? [], [detail]);
  const merlin = useMemo(() => detail?.entries.filter((e) => e.side === "merlin") ?? [], [detail]);
  const provider = useMemo(() => detail?.entries.filter((e) => e.side === "provider") ?? [], [detail]);
  const totals = useMemo(() => selectionTotals(detail?.entries ?? [], sel), [detail, sel]);

  const toggle = (id: number) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = (ids: number[], on: boolean) => setSel((p) => { const n = new Set(p); ids.forEach((i) => (on ? n.add(i) : n.delete(i))); return n; });

  const upload = () => file && run("upload", async () => {
    const form = new FormData(); form.append("file", file); form.append("account", "73662063");
    const r = await brFetch<{ id: number; imported: number }>("/imports", { method: "POST", form });
    setFile(null); if (fileRef.current) fileRef.current.value = "";
    await loadList(); setSelId(r.id);
  }, "Bank statement imported");
  const uploadNominal = (file: File) => selId != null && run("nominal", async () => {
    const form = new FormData(); form.append("file",file); form.append("account","73662063");
    form.append("type","nominal"); form.append("periodId",String(selId));
    const result = await brFetch<{imported:number;matched:number;outsideDateRange:number}>("/imports",{method:"POST",form});
    await refresh();
    setWarnings([`${result.imported.toLocaleString()} nominal entries imported; ${result.matched} evidenced bank matches made automatically. Unmatched items remain highlighted.`,
      ...(result.outsideDateRange ? [`${result.outsideDateRange.toLocaleString()} entries have transaction dates outside this month but belong to the selected accounting period. Original dates are retained.`] : [])]);
  }, "Nominal report imported and checked");
  const removePeriod = async (notes: string) => {
    setBusy("delete");
    try {
      await brFetch(`/periods/${selId}`,{method:"DELETE",json:{notes}});
      setSelId(null); setDetail(null); await loadList(); toast({title:"Period deleted; evidence retained"}); return true;
    } catch(e:any) {toast({title:e.message,variant:"destructive"});return false;}
    finally {setBusy(null);}
  };
  const createMonth = () => run("month", async () => {
    const r = await post("/months", { month }); setMonth(""); await loadList(); setSelId(r.id);
  }, "Month review created");
  const sync = () => selId != null && run("sync", async () => {
    const r = await post(`/periods/${selId}/sync`, {}); setWarnings(r.warnings ?? []); await refresh();
  }, "Ledger evidence refreshed");
  const decide = (kind: string, entryIds: number[], extra: Record<string, unknown> = {}) => selId != null && run(`d-${kind}`, async () => {
    const body: any = { kind, entryIds, notes, ...extra };
    await post(`/periods/${selId}/decisions`, body);
    setSel(new Set()); setNotes(""); setOwner(""); setExpectedDate(""); setEvidence("");
    await refresh();
  }, `Recorded: ${kind}`);
  const applySuggestion = (s: Suggestion) => { setSel(new Set(s.entryIds)); setChannel(s.channel === "unknown" ? "" : s.channel); };
  const confirm = () => {
    const c = poundsToPence(closing);
    if (c == null) return toast({ title: "Enter closing balance in pounds, e.g. 1234.56", variant: "destructive" });
    return run("confirm", async () => { await post(`/periods/${selId}/confirm`, { coverageConfirmed: true, closing: c }); await refresh(); }, "Coverage and closing confirmed");
  };
  const signoff = () => run("signoff", async () => { await post(`/periods/${selId}/signoff`, { notes: signNotes }); setSignNotes(""); await refresh(); }, "Month signed off");
  const reopen = () => run("reopen", async () => { await post(`/periods/${selId}/reopen`, { notes: reopenNotes }); setReopenNotes(""); await refresh(); }, "Month reopened");

  const selIds = [...sel];
  const hasBank = selIds.some((i) => bank.some((e) => e.id === i));
  const hasMerlin = selIds.some((i) => merlin.some((e) => e.id === i));
  const matchBlock = !hasBank || !hasMerlin ? "Select at least one bank and one Merlin entry." : totals.diff !== 0 ? `Totals differ by ${formatPence(totals.diff)}; match requires equality.` : "";
  const needNotes = notes.trim() === "";
  const p = detail?.period;
  const s = detail?.summary;

  return (
    <Layout fullWidth>
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Landmark className="w-6 h-6" />Bank Reconciliation</h1>
          <p className="text-sm text-duffells-grey/70 mt-1">
            Account {list?.account ?? "73662063"} against Merlin nominal {list?.nominal ?? 17012}. Decisions explain entries; nothing here posts to Merlin.
          </p>
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-4 items-start">
          {/* Left rail */}
          <aside className="space-y-3">
            {access.manage && (
              <div className={`${card} p-3 space-y-2`}>
                <h2 className="text-sm font-bold">Add bank statement</h2>
                <p className="text-xs text-slate-600">Weekly or monthly Payment Account CSV. A full-month statement opens its monthly review automatically.</p>
                <input ref={fileRef} id="bank-statement-csv" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="hidden" disabled={busy === "upload"} data-testid="input-import-file" />
                <Button type="button" variant="outline" className="h-auto min-h-12 w-full gap-2 whitespace-normal border-orange-300 bg-orange-50 px-3 py-3 text-orange-700 hover:bg-orange-100 hover:text-orange-800"
                  onClick={() => fileRef.current?.click()} disabled={busy === "upload"} aria-controls="bank-statement-csv" data-testid="button-choose-statement">
                  <FolderOpen className="h-5 w-5 shrink-0" aria-hidden="true" />
                  Choose statement CSV
                </Button>
                <p className="break-all text-xs text-slate-600" role="status">
                  {file ? file.name : "No statement selected"}
                </p>
                <Button size="sm" className="gap-2 w-full" disabled={!file || busy === "upload"} onClick={upload} data-testid="button-import">
                  {busy === "upload" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}Import to account 73662063
                </Button>
                <h2 className="text-sm font-bold pt-2">Create month review</h2>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} data-testid="input-month" />
                <Button size="sm" variant="outline" className="gap-2 w-full" disabled={!month || busy === "month"} onClick={createMonth} data-testid="button-create-month">
                  {busy === "month" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}Create month
                </Button>
                <NominalUpload key={selId} disabled={!detail || detailLoading || detail.period.id!==selId || signed || !!busy} busy={busy==="nominal"} onUpload={uploadNominal} />
              </div>
            )}
            <div className={`${card} overflow-hidden`}>
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-sm font-bold flex justify-between">
                Periods
                <button onClick={() => void loadList()} aria-label="Reload periods" data-testid="button-reload-list"><RefreshCw className="w-3.5 h-3.5" /></button>
              </div>
              {listErr ? (
                <div className="p-4 text-sm text-red-700" data-testid="error-list">{listErr}
                  <Button size="sm" variant="outline" className="mt-2 block" onClick={() => void loadList()}>Retry</Button></div>
              ) : !list ? (
                <div className="p-3 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
              ) : list.periods.length === 0 ? (
                <p className="p-6 text-sm text-center text-duffells-grey/50" data-testid="empty-periods">No periods yet. Import a weekly or monthly bank CSV to begin.</p>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[60vh] overflow-auto">
                  {list.periods.map((x) => (
                    <button key={x.id} onClick={() => setSelId(x.id)} data-testid={`period-${x.id}`}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${selId === x.id ? "bg-orange-50 border-l-4 border-duffells-orange" : ""}`}>
                      <div className="flex justify-between font-semibold"><span>{x.kind==="weekly"?"Bank statement":"Monthly review"}</span>
                        <span className={x.status === "signed" ? "text-green-700" : "text-amber-700"}>{x.status}</span></div>
                      <div className="text-duffells-grey/70">{x.start} to {x.end}</div>
                      <div className="font-mono">{formatPence(x.opening)} / {formatPence(x.closing)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Detail */}
          <section className="space-y-4 min-w-0">
            {selId == null ? (
              <div className={`${card} p-12 text-center text-duffells-grey/60`} data-testid="empty-detail">Choose a period to review its entries.</div>
            ) : detailErr ? (
              <div className={`${card} p-6 text-sm text-red-700`} data-testid="error-detail">{detailErr}
                <Button size="sm" variant="outline" className="ml-3" onClick={() => void loadDetail(selId)}>Retry</Button></div>
            ) : detailLoading && !detail ? (
              <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}</div>
            ) : detail && p && s ? (
              <>
                <div className={card}>
                  <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100">
                    <div>
                      <div className="font-bold capitalize" data-testid="text-period-title">{p.kind} review {p.start} to {p.end}</div>
                      <div className="text-xs text-duffells-grey/60">
                        {p.status === "signed" ? "Signed off" : "Draft"} | coverage {p.coverageConfirmed ? "confirmed" : "not confirmed"} | ledger synced {p.syncedAt ?? "never"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canManage && <Button size="sm" variant="outline" className="gap-1" disabled={!!busy} onClick={sync} data-testid="button-sync">
                        {busy === "sync" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}{p.ledgerProof?.fileHash?"Refresh settlement evidence":"Refresh ledger evidence"}</Button>}
                      <Button asChild size="sm" variant="outline" className="gap-1"><a href={apiUrl(`/periods/${p.id}/export`)} data-testid="link-export"><FileDown className="w-3.5 h-3.5" />Audit JSON</a></Button>
                      {access.admin && <DeletePeriodButton label={`${p.start} to ${p.end}`} disabled={signed || !!busy} onDelete={removePeriod} />}
                    </div>
                  </div>
                  {p.ledgerProof?.fileHash && <p className="px-4 py-2 text-xs bg-blue-50 text-blue-900 border-t">
                    Ledger basis: uploaded accounting-period trial balance. Original transaction dates are retained, including {p.ledgerProof.outsideDateRange ?? 0} entries dated outside this month. Settlement refresh keeps this uploaded ledger unchanged.
                  </p>}
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 divide-x divide-slate-100">
                    <Stat id="opening" label="Bank opening" value={formatPence(s.opening)} />
                    <Stat id="in" label="Money in" value={formatPence(s.bankIn)} />
                    <Stat id="out" label="Money out" value={formatPence(s.bankOut)} />
                    <Stat id="closing" label="Bank closing" value={formatPence(s.closing)} />
                    <Stat id="ledger-opening" label="Ledger opening" value={formatPence(s.ledgerOpening)} />
                    <Stat id="ledger-closing" label="Ledger closing" value={formatPence(s.ledgerClosing)} />
                    <Stat id="difference" label="Difference" value={s.difference == null ? "Not synced" : formatPence(s.difference)} tone={s.difference === 0 ? "text-green-700" : "text-red-700"} />
                    <Stat id="unresolved" label="Unresolved" value={String(s.unresolved)} tone={s.unresolved ? "text-red-700" : "text-green-700"} />
                  </div>
                  <div className="px-4 py-2 border-t border-slate-100 flex flex-wrap gap-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 font-medium" data-testid="aged-exceptions">Aged exceptions (7+ days): {s.agedExceptions}</span>
                    {Object.entries(s.counts).map(([k, v]) => (
                      <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 font-medium" data-testid={`count-${k}`}>{STATUS_LABEL[k] ?? k}: {v}</span>
                    ))}
                  </div>
                </div>

                {(s.blockers.length > 0 || warnings.length > 0 || !s.ready) && (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm space-y-1" data-testid="panel-blockers">
                    <div className="font-bold flex items-center gap-2 text-amber-800"><AlertTriangle className="w-4 h-4" />
                      {s.ready ? "Ready with warnings" : "Not ready: not reconciled"}</div>
                    {s.blockers.map((b, i) => <div key={`b${i}`} className="text-red-800">Blocker: {b}</div>)}
                    {warnings.map((w, i) => <div key={`w${i}`} className="text-amber-800">Warning: {w}</div>)}
                  </div>
                )}

                {detail.suggestions.length > 0 && (
                  <div className={`${card} p-3`} data-testid="panel-suggestions">
                    <h3 className="text-sm font-bold mb-2">Suggestions (review only, based on reference evidence)</h3>
                    <div className="space-y-1.5">
                      {detail.suggestions.map((sg, i) => (
                        <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-xs bg-slate-50 rounded p-2">
                          <div><span className="uppercase font-semibold">{sg.channel}</span> {sg.basis}
                            <span className="font-mono ml-2">bank {formatPence(sg.bankAmount)} / ledger {formatPence(sg.ledgerAmount)}</span>
                            <span className="ml-2 text-duffells-grey/60">{sg.entryIds.length} entries</span></div>
                          <Button size="sm" variant="outline" onClick={() => applySuggestion(sg)} data-testid={`button-suggestion-${i}`}>Select entries</Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid xl:grid-cols-2 gap-4">
                  <EntryTable testid="bank" title="Bank entries" entries={bank} selected={sel} onToggle={toggle} onToggleAll={toggleAll} selectable={canManage} />
                    <EntryTable testid="merlin" title={p.ledgerProof?.fileHash ? "Uploaded nominal 17012 entries" : "Merlin 17012 entries"} entries={merlin} selected={sel} onToggle={toggle} onToggleAll={toggleAll} selectable={canManage} />
                </div>
                {provider.length > 0 && (
                  <EntryTable testid="provider" title="Provider payout evidence" entries={provider} selected={sel} onToggle={toggle} onToggleAll={toggleAll} selectable={canManage} />
                )}

                {canManage && (
                  <div className={`${card} p-3 space-y-3 sticky bottom-2 shadow-lg`} data-testid="panel-decision">
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono">
                      <span className="font-sans font-bold">{sel.size} selected</span>
                      <span>Bank {formatPence(totals.bank)}</span><span>Merlin {formatPence(totals.merlin)}</span>
                      {totals.provider !== 0 && <span>Provider {formatPence(totals.provider)}</span>}
                      <span className={totals.diff === 0 ? "text-green-700" : "text-red-700"} data-testid="text-selection-diff">Difference {formatPence(totals.diff)}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (required)" rows={2} className={`${inputCls} lg:col-span-2`} data-testid="input-notes" />
                      <select value={channel} onChange={(e) => setChannel(e.target.value)} className={inputCls} data-testid="select-channel">
                        <option value="">Channel (match)</option><option value="bacs">BACS</option><option value="stripe">Stripe</option><option value="adflex">Adflex</option>
                      </select>
                      <input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Supporting evidence" className={inputCls} data-testid="input-evidence" />
                      <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Owner (timing)" className={inputCls} data-testid="input-owner" />
                      <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className={inputCls} data-testid="input-expected-date" aria-label="Expected date" />
                    </div>
                    {matchBlock && sel.size > 0 && <p className="text-xs text-amber-800">{matchBlock}</p>}
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" disabled={!!matchBlock || needNotes || !channel || !evidence.trim() || !!busy}
                        onClick={() => decide("match", selIds, { channel, evidence, matchType: selIds.length > 2 ? "many" : "one_to_one" })} data-testid="button-match">Match selected</Button>
                      <Button size="sm" variant="outline" disabled={sel.size === 0 || needNotes || !!busy} onClick={() => decide("reject", selIds)} data-testid="button-reject">Reject match</Button>
                      <Button size="sm" variant="outline" disabled={sel.size === 0 || needNotes || !owner.trim() || !expectedDate || !evidence.trim() || !!busy}
                        onClick={() => decide("timing", selIds, { owner, expectedDate, evidence })} data-testid="button-timing">Mark timing difference</Button>
                      <Button size="sm" variant="outline" disabled={sel.size === 0 || needNotes || !!busy} onClick={() => decide("note", selIds)} data-testid="button-note">Add note</Button>
                    </div>
                  </div>
                )}

                {canManage && (
                  <div className={`${card} p-3 space-y-2`} data-testid="panel-confirm">
                    <h3 className="text-sm font-bold">Confirm coverage and statement closing balance</h3>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={coverage} onChange={(e) => setCoverage(e.target.checked)} data-testid="check-coverage" />Source coverage is complete</label>
                      <input value={closing} onChange={(e) => setClosing(e.target.value)} placeholder="Closing balance, e.g. 1234.56" className={`${inputCls} max-w-xs`} data-testid="input-closing" />
                      <Button size="sm" variant="outline" disabled={!coverage || !closing || busy === "confirm"} onClick={confirm} data-testid="button-confirm">Confirm</Button>
                    </div>
                    <p className="text-xs text-duffells-grey/60">Confirms the source evidence only. It does not adjust the ledger.</p>
                  </div>
                )}

                {p.kind === "monthly" && (
                  <div className={`${card} p-3 space-y-2`} data-testid="panel-signoff">
                    <h3 className="text-sm font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Month sign-off</h3>
                    {signed ? (
                      <>
                        <pre className="text-[11px] bg-slate-50 border rounded p-2 overflow-auto">{JSON.stringify(p.signoff, null, 2)}</pre>
                        {access.signoff ? (
                          <div className="flex gap-2 flex-wrap">
                            <input value={reopenNotes} onChange={(e) => setReopenNotes(e.target.value)} placeholder="Reason for reopening (required)" className={`${inputCls} max-w-md`} data-testid="input-reopen-notes" />
                            <Button size="sm" variant="outline" className="gap-1" disabled={!reopenNotes.trim() || busy === "reopen"} onClick={reopen} data-testid="button-reopen-month"><RotateCcw className="w-3.5 h-3.5" />Reopen month</Button>
                          </div>
                        ) : <p className="text-xs">Reopening needs the sign-off permission.</p>}
                      </>
                    ) : access.signoff ? (
                      <div className="flex gap-2 flex-wrap">
                        <input value={signNotes} onChange={(e) => setSignNotes(e.target.value)} placeholder="Sign-off notes" className={`${inputCls} max-w-md`} data-testid="input-signoff-notes" />
                        <Button size="sm" disabled={!s.ready || !signNotes.trim() || busy === "signoff"} onClick={signoff} data-testid="button-signoff">Sign off month</Button>
                        {!s.ready && <span className="text-xs text-red-700 self-center">Server reports this month is not ready.</span>}
                      </div>
                    ) : <p className="text-xs text-duffells-grey/70">You do not have the sign-off permission. Sign-off must be by a separate reviewer.</p>}
                  </div>
                )}

                <div className="grid lg:grid-cols-2 gap-4">
                  <div className={`${card} overflow-hidden`} data-testid="panel-decisions">
                    <div className="px-3 py-2 bg-slate-50 border-b text-sm font-bold">Decision history (append-only)</div>
                    {detail.decisions.length === 0 ? <p className="p-4 text-xs text-center text-duffells-grey/50">No decisions recorded.</p> : (
                      <div className="divide-y max-h-80 overflow-auto">
                        {[...detail.decisions].reverse().map((d) => (
                          <div key={d.id} className="px-3 py-2 text-xs" data-testid={`decision-${d.id}`}>
                            <div className="flex justify-between"><span className="font-bold uppercase">{d.kind}</span><span className="text-duffells-grey/60">{d.createdAt} by {String(d.actorId ?? "")}</span></div>
                            <div>Entries: {d.entryIds.join(", ")}</div>
                            <div className="text-duffells-grey/70 break-words">{Object.entries(d.details ?? {}).filter(([, v]) => v != null && v !== "").map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`).join(" | ")}</div>
                            {d.kind === "match" && canManage && (
                              <Button size="sm" variant="outline" className="mt-1 h-6 text-[11px]" disabled={needNotes || !!busy}
                                title={needNotes ? "Enter notes above first" : "Reopen this whole match"}
                                onClick={() => decide("reopen", d.entryIds)} data-testid={`button-reopen-match-${d.id}`}>Reopen match</Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={`${card} overflow-hidden`} data-testid="panel-imports">
                    <div className="px-3 py-2 bg-slate-50 border-b text-sm font-bold">Source files</div>
                    {detail.imports.length === 0 ? <p className="p-4 text-xs text-center text-duffells-grey/50">No imports in range.</p> : (
                      <div className="divide-y">
                        {detail.imports.map((im) => (
                          <div key={im.id} className="px-3 py-2 text-xs flex justify-between gap-2 items-center">
                            <div className="min-w-0"><div className="font-medium truncate">{im.filename}</div>
                              <div className="text-duffells-grey/60">{im.rowCount} rows, {im.createdAt}</div></div>
                            <Button asChild size="sm" variant="outline" className="gap-1"><a href={apiUrl(`/imports/${im.id}/file`)} data-testid={`link-source-${im.id}`}><Download className="w-3 h-3" />Source</a></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </section>
        </div>
      </div>
    </Layout>
  );
}
